/* global _TL_ */
import Backbone from 'backbone';
import _ from 'underscore';
import React from 'react';
import ReactDOM from 'react-dom';
import Promise from 'bluebird';
import { ThemeProvider } from 'react-jss';
import { ComponentSettings, ThemeFluent } from '@bingads-webui-theme-2018/theme-fluent';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { InstrumentationContext } from '@bingads-webui-react/hoc-with-instrumentation';
import { saveAds, saveAdsWithMetadata } from '@bingads-webui-campaign-react/display-plus-native-ads';
import { Scenario, Activity } from '@bingads-webui-react/hoc-with-signal';
import { ServerContext } from '@bingads-webui-campaign-react/server-context';
import { PreferenceContext } from '@bingads-webui-react/preference-context';
import { ScopeContext } from '@bingads-webui-react/scope-service';
import { UiBlocker } from '@bingads-webui-react/ui-blocker';
import { PermissionsContext } from '@bingads-webui-react/hoc-with-permissions';
import { PerformanceMarkerContext } from '@bingads-webui-campaign-react/performance-marker-provider';
import { getVerifiedTrackingSettings } from '@bingads-webui-campaign-react/xandr-display-ads-campaign-shared-components';
import { getODataConfig } from '@bingads-webui-campaign-react/xandr-video-campaign-shared-components';
import { ImageOData } from '@bingads-webui-campaign/image-odata';
import { DisplayPlusNative } from '../../components';

export default class View extends Backbone.View {
  initialize({
    scope,
    editMode = false,
    model = {},
    showInline = false,
    instrumentation,
    perfMarker,
    permissions,
    activityLogger,
    campaignId,
    adGroupId,
    serverContext,
    preferenceService,
  }) {
    this.aid = _.get(serverContext, ['CurrentAccount', 'Id']);
    this.cid = _.get(serverContext, ['CurrentCustomer', 'Id']);
    this.advertiserCustomerId = _.get(serverContext, ['CurrentAccount', 'AdvertiserCustomerID']);
    this.scope = scope;
    this.editMode = editMode;
    this.showInline = showInline;
    this.perfMarker = perfMarker;
    this.instrumentation = instrumentation;
    this.model = model;
    this.apiErrors = [];
    this.campaignId = campaignId;
    this.adGroupId = adGroupId;
    this.activityLogger = activityLogger;
    this.resetFields = false;
    this.permissions = permissions;
    this.serverErrors = null;
    this.serverContext = serverContext;
    this.preferenceService = preferenceService;
    this.verifiedTrackingSetting = this.editMode ? getVerifiedTrackingSettings(model) : {};
    this.model = {
      ...this.model,
      ...this.verifiedTrackingSetting,
    };
    this.isLoading = false;
    this.isSaving = false;
    this.isCanceling = false;
    this.saveFailed = false;
    this.isCleaning = false;

    this.oDataConfig = getODataConfig(serverContext);

    this.imageOdata = new ImageOData(this.cid, this.aid, this.oDataConfig);

    this.scenario = 'Edit-DisplayPlusNativeAds';

    this.savedAdModel = {
      ...this.translateHotspots(this.model),
      isValid: true,
      isDirty: false,
      currentPageIndex: 1,
    };

    this.isVideoAsAnAssetEnabled = this.permissions && this.permissions.dynamic.VideoAsAnAsset && !this.permissions.IsAmplifyEnabled;
    this.isImageFittingEnabled = _.result(permissions.dynamic, 'ImageFittingMVP', false) &&
      !_.result(permissions.dynamic, 'ImageFittingExlcudeMAE', true) &&
      _.result(permissions.dynamic, 'ImageFittingUnifiedImageROI', false);
  }

  translateHotspots(model) {
    if (model && model.HotSpots) {
      const hotspots = model.HotSpots.map((hotspot) => ({
        glyph: hotspot.Asset.Glyph,
        actionText: hotspot.Asset.ActionText,
        assetSource: hotspot.Asset.AssetSource,
        id: hotspot.Asset.Id,
        desc: hotspot.Asset.Description,
        placement: hotspot.Asset.Placement,
        title: hotspot.Asset.Title,
        uri: hotspot.Asset.Uri,
      }));

      return {
        ...model,
        hotspots,
      };
    }

    return {
      ...model,
    };
  }

  onAdsValidation(result) {
    this.savedAdModel = { ...result };
    this.render();
  }

  validate() {
    return this.savedAdModel.isValid;
  }

  toObject() {
    return this.savedAdModel;
  }

  clearApiErrors() {
    this.apiErrors = [];
  }

  clearFields() {
    this.editMode = false;
    this.model = {};
    this.resetFields = !this.resetFields;
    this.savedAdModel = {
      isValid: false,
      isDirty: false,
      data: {
        data: [],
      },
      currentPageIndex: 1,
    };

    this.serverErrors = null;

    this.isCleaning = true;
    this.render();
  }

  onCleaningComplete() {
    this.isCleaning = false;
    this.render();
  }

  remove() {
    ReactDOM.unmountComponentAtNode(this.el);
    return super.remove();
  }

  setSavingState() {
    this.isSaving = true;
    this.saveFailed = false;
    this.render();
  }

  buildSaveAdsPayload() {
    return {
      ads: { data: this.savedAdModel },
      oldAdsModel: { data: this.savedAdModel },
      campaign: { Id: this.scope().adGroupId },
      adGroup: { Id: this.scope().adGroupId },
      activity: this.activityLogger,
      imageOdata: this.imageOdata,
      permissions: this.permissions,
    };
  }

  onSaveAdsSuccess(updateGrid) {
    return (result) => {
      if (result && result.ads && result.ads.data) {
        this.clearFields();
        return updateGrid(result.ads.data, result.ads.data.Id, 'Responsive');
      }
      return result;
    };
  }

  onSaveAdsFailed(result) {
    this.serverErrors = result.serverErrors || result;
    this.saveFailed = true;
  }

  onSaveAdsFinally() {
    this.isSaving = false;
    this.render();
  }

  saveAdAction(updateGrid) {
    const saveAdsAction = this.isVideoAsAnAssetEnabled ? saveAdsWithMetadata : saveAds;

    const saveAdsPayload = this.buildSaveAdsPayload();

    return saveAdsAction(saveAdsPayload, { scenario: this.scenario, i18n: this.options.newI18n, serverContext: this.serverContext, activityLogger: this.activityLogger }, { isImageFittingEnabled: this.isImageFittingEnabled })
      .then(this.onSaveAdsSuccess(updateGrid))
      .catch(this.onSaveAdsFailed.bind(this))
      .finally(this.onSaveAdsFinally.bind(this));
  }

  saveAdAsync(updateGrid) {
    if (!this.validate()) {
      return Promise.resolve();
    }

    this.setSavingState();
    return this.saveAdAction(updateGrid);
  }

  saveAndCreateAnotherAsync(updateGrid) {
    if (!this.validate()) {
      return Promise.resolve();
    }

    this.setSavingState();
    return this.saveAdAction(updateGrid);
  }

  render() {
    const scenarioName = 'DisplayPlusNativeAdsAdEditing';
    const activityName = 'Ad';

    ReactDOM.render(
      <I18nContext.Provider value={this.options.newI18n}>
        <InstrumentationContext.Provider value={this.instrumentation}>
          <ThemeProvider theme={{ components: ComponentSettings, core: ThemeFluent }}>
            <ScopeContext.Provider value={{ scope: { values: { ...this.scope(), cid: this.cid, aid: this.aid } } }}>
              <Scenario name={scenarioName}>
                <Activity name={activityName}>
                  <ServerContext.Provider value={this.serverContext}>
                    <PermissionsContext.Provider value={this.permissions}>
                      <PerformanceMarkerContext.Provider value={this.perfMarker}>
                        <PreferenceContext.Provider value={this.preferenceService}>
                          <UiBlocker
                            blocking={this.isLoading || this.isSaving}
                            loadingText={this.options.newI18n.getString(this.isSaving ? _TL_('Saving...') : _TL_('Loading...'))}
                            classes={{
                              blockUiContainer: 'blocker-global',
                              blockerIndicator: 'blocker-indicator',
                            }}
                          >
                            {this.isLoading ?
                              <div style={{ height: '412px' }}></div> :
                              <DisplayPlusNative
                                data={this.savedAdModel}
                                onDataChange={this.onAdsValidation.bind(this)}
                                perfMarker={this.perfMarker}
                                instrumentation={this.instrumentation}
                                scope={this.scope()}
                                isSaving={this.isSaving}
                                isCanceling={this.isCanceling}
                                saveFailed={this.saveFailed}
                                cid={this.cid}
                                aid={this.aid}
                                campaignId={this.campaignId}
                                adGroupId={this.adGroupId}
                                errors={this.serverErrors}
                                isCleaning={this.isCleaning}
                                onCleaningComplete={this.onCleaningComplete.bind(this)}
                                permissions={this.permissions}
                                supportImageFitting={this.isImageFittingEnabled}
                              />
                            }
                          </UiBlocker>
                        </PreferenceContext.Provider>
                      </PerformanceMarkerContext.Provider>
                    </PermissionsContext.Provider>
                  </ServerContext.Provider>
                </Activity>
              </Scenario>
            </ScopeContext.Provider>
          </ThemeProvider>
        </InstrumentationContext.Provider>
      </I18nContext.Provider>
      ,
      this.el,
    );

    return this;

  }
}
